import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { Subscription, tap } from 'rxjs';

import { FieldConfig } from '@shared/restaurant/models';
import { FieldType } from '@shared/restaurant/enums';
import {
  emailRegexp,
  urlValidator,
  ValidationService,
  CustomDateParserFormatterProvider,
} from '@tablesready/common-ui';
import { CustomDateAdapterProvider } from './providers';
import { AppIcon } from '@tablesready/frontend-icons';

interface FormValidators {
  validators: Validators[];
  asyncValidators: AsyncValidator[] | AsyncValidatorFn[];
  updateOn?: string;
}

@Component({
  selector: 'tablesready-customer-custom-fields',
  templateUrl: './customer-custom-fields.component.html',
  styleUrls: ['./customer-custom-fields.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerCustomFieldsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: CustomerCustomFieldsComponent,
      multi: true,
    },
    CustomDateAdapterProvider,
    CustomDateParserFormatterProvider,
  ],
})
export class CustomerCustomFieldsComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, Validator {
  @Input() config: FieldConfig[];
  @Input() country: string = 'US';
  group: FormGroup;
  fieldTypes = FieldType;
  value: any = {};
  icon = AppIcon;

  private onChangeMethod: (value: number | string | boolean) => unknown;
  private onTouchedMethod: () => unknown;
  private readonly subscriptions = new Subscription();
  private validatorsByType: {[key: string]: FormValidators} = {
    [FieldType.text]: { validators: [], asyncValidators: [] },
    [FieldType.number]: { validators: [], asyncValidators: [] },
    [FieldType.email]: {
      validators: [Validators.pattern(emailRegexp)],
      asyncValidators: [],
    },
    [FieldType.url]: { validators: [urlValidator], asyncValidators: [] },
    [FieldType.tel]: {
      validators: [this.validationService.phonePatternCountryValidator(() => this.country)],
      asyncValidators: [],
    },
    [FieldType.date]: { validators: [], asyncValidators: [] },
    [FieldType.select]: { validators: [], asyncValidators: [] },
    [FieldType.multiSelect]: { validators: [], asyncValidators: [] },
  };

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly validationService: ValidationService,
  ) {
  }

  ngOnInit(): void {
    this.initControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.initControls();
      this.group?.patchValue(this.value);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  registerOnChange(onChange: (_: any) => void): void {
    this.onChangeMethod = onChange;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedMethod = fn;
  }

  writeValue(value: any) {
    if (value === null) {
      this.group?.reset();

      return;
    }

    this.value = value || {};
    this.group?.patchValue(value || {});
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.group.disable() : this.group.enable();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.group?.invalid && { invalid: true } || null;
  }

  getFields() {
    return this.config?.filter(field => field.enabled && field.customer);
  }

  private initControls(): void {
    const definition: {[key: string]: [null, FormValidators]} = {};
    const fields = this.getFields();

    if (fields && fields.length > 0) {
      for (const field of fields) {
        const rules = this.validatorsByType[field.type];
        if(field.visibility.private.required) {
          rules.validators.unshift(Validators.required);
        }

        definition[field.id] = [null, rules];
      }

      this.group = this.formBuilder.group(definition);
      const valueChangesSubscription = this.group.valueChanges.pipe(
        tap(value => {
          if (this.onChangeMethod) {
            this.onChangeMethod(value);
          }
        }),
      )
        .subscribe();
      this.subscriptions.add(valueChangesSubscription);
    }
  }
}
